// ── Session recordings status section (GATED-DARK, status-only) ─────────────
// Wave 6 (Sessions). Recordings are a GATED-DARK lane: the backend read is
// role-scoped (F1) and returns a secure link + passcode PRESENCE only — the raw
// passcode and public URL are NEVER returned (the DTO pins rawPasscodeReturned /
// publicUrlReturned to false). This panel shows STATUS only: how many recordings
// exist + whether each carries a passcode (presence), never the passcode itself,
// never a raw/public URL. Until ingest the list is empty -> honest "unavailable".
//
// The Z6 read hook (useCoachingRecordings) is live-only; in mock/evidence mode it
// returns data:null with isLiveBound:false, which we render as the honest
// "not yet available" status (NOT an error, NOT a fabricated recording).

import { Alert, Badge, Module, ModuleBody, ModuleHead, SkeletonRows } from "@vjal/ui";

import { useCoachingRecordings } from "../../../hooks/useCoachingZ6Data";

export function SessionRecordingsSection({ sessionId }: { sessionId: string }) {
  const binding = useCoachingRecordings(sessionId);
  const recordings = binding.data?.recordings;
  const entries = recordings?.entries ?? [];

  return (
    <Module span={6}>
      <ModuleHead title="Recordings" subtitle="Recording availability — status only, never a passcode or raw link." />
      <ModuleBody className="space-y-3">
        {binding.denied ? (
          <div data-honest-posture="permission-denied">
            <Alert variant="info">Your role can't view recordings for this session.</Alert>
          </div>
        ) : binding.isError ? (
          <div data-honest-posture="unavailable">
            <Alert variant="error">
              Recordings are temporarily unavailable{binding.reference ? ` (reference: ${binding.reference})` : ""}.
            </Alert>
          </div>
        ) : binding.isLoading ? (
          <div data-honest-posture="loading" aria-label="Loading recordings">
            <SkeletonRows rows={2} columns={2} className="rounded-lg border border-line" />
          </div>
        ) : entries.length === 0 ? (
          <div data-honest-posture="unavailable">
            <Alert variant="info">
              No recordings are available yet. Recordings appear here after the session is held and processed.
            </Alert>
          </div>
        ) : (
          <ul className="m-0 list-none space-y-2 p-0" data-honest-posture-ready="recordings">
            {entries.map((entry) => (
              <li key={entry.id} className="flex items-center justify-between gap-3 text-sm text-ink-body">
                <span>{entry.recordingType}</span>
                <Badge variant={entry.passcodePresent ? "warning" : "default"}>
                  {entry.passcodePresent ? "passcode protected" : "no passcode"}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </ModuleBody>
    </Module>
  );
}
